import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, Download, Pencil, Trash2, Shuffle, Check as CheckIcon, Mail, MessageSquare, ExternalLink } from "lucide-react";
import { Card } from "./AdminLayout";
import PromoBuilder from "./PromoBuilder";
import {
  adminPromotion, adminPromotionEntries, adminPromotionResults, adminDeletePromotion, adminDownloadLeads,
  adminDrawWinner, adminApprovePromoPhoto, adminDeletePromoPhoto, adminUpdatePromotion, TYPE_META,
} from "../lib/promotions";

const TABS = [["entries", "Entries & Leads"], ["results", "Results"], ["emails", "Emails"], ["texting", "Texting"]];

export default function PromoDetail({ id, onBack, onEdit, builder, closeBuilder }) {
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("entries");
  const load = () => adminPromotion(id).then(setC).catch(() => {});
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!c) return null;
  const remove = async () => { if (!window.confirm(`Delete "${c.title}" and all its entries?`)) return; await adminDeletePromotion(c.id); onBack(); };
  return (
    <div data-testid="promo-detail">
      <button onClick={onBack} data-testid="promo-back" className="mb-3 flex items-center gap-1 text-sm font-semibold text-slate-500"><ChevronLeft size={16} /> All campaigns</button>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div><p className="text-xs font-bold uppercase tracking-wider text-[var(--gold)]">{TYPE_META[c.type]?.label}{c.sponsor_name && ` · ${c.sponsor_name}`}</p><h2 className="text-2xl font-bold text-[var(--navy)]">{c.title}</h2></div>
        <div className="flex gap-2">
          <a href={`/contests/${c.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600"><ExternalLink size={14} /> View</a>
          <button onClick={() => onEdit(c)} data-testid="promo-edit" className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600"><Pencil size={14} /> Edit</button>
          <button onClick={remove} data-testid="promo-delete" className="flex items-center gap-1 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600"><Trash2 size={14} /></button>
        </div>
      </div>
      <div className="no-scrollbar mb-4 flex gap-2 overflow-x-auto">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`promo-detail-tab-${k}`} className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold ${tab === k ? "bg-[var(--navy)] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{l}</button>)}
      </div>
      {tab === "entries" && <Entries c={c} />}
      {tab === "results" && <Results c={c} />}
      {tab === "emails" && <Emails c={c} reload={load} />}
      {tab === "texting" && <Texting c={c} />}
      {builder && <PromoBuilder initial={builder} onClose={closeBuilder} onSaved={() => { closeBuilder(); load(); toast.success("Campaign saved."); }} />}
    </div>
  );
}

function Entries({ c }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { adminPromotionEntries(c.id).then(setRows).catch(() => {}); }, [c.id]);
  const lqs = c.lead_questions || [];
  return (
    <Card className="overflow-x-auto">
      <div className="flex items-center justify-between border-b border-slate-100 p-4">
        <p className="text-sm text-slate-500"><b className="text-[var(--navy)]">{rows.length}</b> entries · {rows.filter((r) => r.opt_in_sponsor).length} sponsor opt-ins</p>
        <button onClick={() => adminDownloadLeads(c.id, c.title)} data-testid="promo-export-leads" className="flex items-center gap-1.5 rounded-lg bg-[var(--navy)] px-3 py-2 text-xs font-semibold text-white"><Download size={14} /> Sponsor lead report (CSV)</button>
      </div>
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Entrant</th><th className="px-4 py-3">When</th><th className="px-4 py-3">Result</th>{lqs.map((q) => <th key={q.id} className="px-4 py-3">{q.label}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={r.id || i} data-testid={`promo-entry-${i}`}>
              <td className="px-4 py-3"><p className="font-semibold text-[var(--navy)]">{r.name}</p><p className="text-xs text-slate-400">{r.email}{r.zip ? ` · ${r.zip}` : ""}{r.member_id ? " · Loyalty member" : ""}</p></td>
              <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.entered_at).toLocaleString()}</td>
              <td className="px-4 py-3 text-xs">{r.quiz_result || (r.quiz_score !== undefined ? `${r.quiz_score}/${r.quiz_total}` : r.votes ? `${Object.keys(r.votes).length} votes` : "—")}</td>
              {lqs.map((q) => <td key={q.id} className="px-4 py-3 text-xs">{r.answers?.[q.id] || "—"}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={3 + lqs.length} className="px-4 py-6 text-center text-slate-400">No entries yet.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function Results({ c }) {
  const [r, setR] = useState(null);
  const load = () => adminPromotionResults(c.id).then(setR).catch(() => {});
  useEffect(() => { load(); }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return null;
  const draw = async () => { try { await adminDrawWinner(c.id); load(); toast.success("Winner drawn."); } catch (e) { toast.error(e.response?.data?.detail || "Could not draw."); } };
  return (
    <div className="space-y-4" data-testid="promo-results">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Entries" value={r.entries} /><Stat label="Unique people" value={r.unique_entrants} />
      </div>
      {r.type === "sweepstakes" && (
        <Card className="p-5">
          <p className="font-bold text-[var(--navy)]">Winner</p>
          {r.winner ? <p className="mt-1 text-sm" data-testid="promo-winner"><b>{r.winner.name}</b> · {r.winner.email} · drawn {new Date(r.winner.drawn_at).toLocaleString()} from {r.winner.pool} people</p> : <p className="mt-1 text-sm text-slate-500">No winner drawn yet.</p>}
          <button onClick={draw} data-testid="promo-draw-winner" className="mt-3 flex items-center gap-1.5 rounded-lg bg-[var(--gold)] px-4 py-2 text-sm font-semibold text-white"><Shuffle size={15} /> {r.winner ? "Redraw" : "Draw a random winner"}</button>
        </Card>
      )}
      {r.categories?.map((cat) => (
        <Card key={cat.id} className="p-5">
          <p className="font-bold text-[var(--navy)]">{cat.name}</p>
          {cat.nominees.map((n, i) => <Bar key={n.id} label={n.name} value={n.votes} max={cat.nominees[0]?.votes || 1} lead={i === 0 && n.votes > 0} />)}
        </Card>
      ))}
      {r.distribution && (
        <Card className="p-5"><p className="font-bold text-[var(--navy)]">{c.quiz_kind === "trivia" ? "Scores" : "Results"}</p>
          {r.distribution.map((d) => <Bar key={d.label} label={c.quiz_kind === "trivia" ? `${d.label} correct` : d.label} value={d.count} max={r.distribution[0]?.count || 1} />)}
          {r.distribution.length === 0 && <p className="text-sm text-slate-400">No plays yet.</p>}
        </Card>
      )}
      {r.photos && <PhotoModeration c={c} photos={r.photos} reload={load} />}
    </div>
  );
}

const Stat = ({ label, value }) => <Card className="p-4"><p className="text-2xl font-bold text-[var(--navy)]">{value}</p><p className="text-xs uppercase text-slate-400">{label}</p></Card>;
const Bar = ({ label, value, max, lead }) => (
  <div className="mt-2">
    <div className="flex justify-between text-sm"><span className={lead ? "font-bold text-[var(--navy)]" : "text-slate-600"}>{label}</span><span className="font-semibold">{value}</span></div>
    <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-full rounded-full bg-[var(--gold)]" style={{ width: `${(value / max) * 100}%` }} /></div>
  </div>
);

function PhotoModeration({ c, photos, reload }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {photos.map((p) => (
        <Card key={p.id} className="overflow-hidden" data-testid={`promo-photo-${p.id}`}>
          <img src={p.image} alt={p.caption} className="h-56 w-full object-cover object-center" />
          <div className="flex items-center justify-between gap-2 p-3">
            <div className="min-w-0"><p className="truncate text-sm font-semibold">{p.caption}</p><p className="text-xs text-slate-400">{p.name} · {p.votes} votes · {p.status}</p></div>
            <div className="flex gap-1">
              {p.status === "pending" && <button onClick={async () => { await adminApprovePromoPhoto(c.id, p.id); reload(); }} data-testid={`promo-approve-photo-${p.id}`} className="rounded-lg bg-emerald-50 p-2 text-emerald-600"><CheckIcon size={15} /></button>}
              <button onClick={async () => { if (window.confirm("Remove photo?")) { await adminDeletePromoPhoto(c.id, p.id); reload(); } }} data-testid={`promo-delete-photo-${p.id}`} className="rounded-lg bg-red-50 p-2 text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

const EMAILS = [["kickoff", "Kick-off", "Sent when the campaign launches"], ["last_chance", "Last chance", "Sent a few days before it closes"], ["thank_you", "Thank-you + sponsor offer", "Sent right after someone enters"]];
const fill = (s, c) => (s || "").replaceAll("{title}", c.title).replaceAll("{sponsor}", c.sponsor_name || "our sponsor").replaceAll("{prize}", c.prize || "the prize").replaceAll("{coupon}", c.sponsor_coupon || "").replaceAll("{name}", "Jane");

function Emails({ c, reload }) {
  const [emails, setEmails] = useState(c.emails || {});
  const save = async () => { await adminUpdatePromotion(c.id, { ...c, emails }); toast.success("Email templates saved."); reload(); };
  return (
    <div className="space-y-4" data-testid="promo-emails">
      <p className="rounded-lg bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800" data-testid="promo-emails-not-sending">Not sending yet: preview and edit only. Placeholders: {"{name} {title} {sponsor} {prize} {coupon}"}</p>
      {EMAILS.map(([k, label, when]) => {
        const e = emails[k] || { subject: "", body: "" };
        const upd = (patch) => setEmails({ ...emails, [k]: { ...e, ...patch } });
        return (
          <Card key={k} className="grid gap-4 p-5 lg:grid-cols-2">
            <div>
              <p className="flex items-center gap-1.5 font-bold text-[var(--navy)]"><Mail size={15} /> {label}</p><p className="mb-2 text-xs text-slate-400">{when}</p>
              <input data-testid={`email-subject-${k}`} value={e.subject} onChange={(ev) => upd({ subject: ev.target.value })} className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Subject" />
              <textarea data-testid={`email-body-${k}`} rows={6} value={e.body} onChange={(ev) => upd({ body: ev.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`email-preview-${k}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Preview</p>
              <p className="mt-1 font-semibold text-[var(--navy)]">{fill(e.subject, c)}</p>
              <p className="mt-2 whitespace-pre-line text-sm text-slate-600">{fill(e.body, c)}</p>
              {k === "thank_you" && c.sponsor_name && <p className="mt-3 text-[11px] text-slate-400">Sponsored by {c.sponsor_name}</p>}
            </div>
          </Card>
        );
      })}
      <button onClick={save} data-testid="promo-emails-save" className="rounded-lg bg-[var(--navy)] px-5 py-2 text-sm font-semibold text-white">Save templates</button>
    </div>
  );
}

function Texting({ c }) {
  const keys = c.type === "ballot"
    ? (c.categories || []).flatMap((cat) => cat.nominees.filter((n) => n.sms_keyword).map((n) => ({ kw: n.sms_keyword, what: `Vote for ${n.name} (${cat.name})` })))
    : c.sms_keyword ? [{ kw: c.sms_keyword, what: "Enter this campaign" }] : [];
  return (
    <div className="space-y-4" data-testid="promo-texting">
      <p className="flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-600" data-testid="promo-texting-status"><MessageSquare size={15} /> Texting is not connected yet. Keywords are saved and will work once a texting number is added.</p>
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Keyword</th><th className="px-4 py-3">What it does</th><th className="px-4 py-3">Texts received</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {keys.map((k) => <tr key={k.kw} data-testid={`sms-keyword-${k.kw}`}><td className="px-4 py-3 font-mono font-bold text-[var(--navy)]">{k.kw}</td><td className="px-4 py-3">{k.what}</td><td className="px-4 py-3 text-slate-400">0</td></tr>)}
            {keys.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">No keywords yet. Add one in Edit.</td></tr>}
          </tbody>
        </table>
      </Card>
      {keys[0] && (
        <Card className="max-w-sm p-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Sample conversation</p>
          <p className="ml-auto w-fit rounded-2xl rounded-br-sm bg-[var(--navy)] px-3 py-2 text-sm text-white">{keys[0].kw}</p>
          <p className="mt-2 w-fit rounded-2xl rounded-bl-sm bg-slate-100 px-3 py-2 text-sm text-slate-700">Nassau Life: Thanks! {c.type === "ballot" ? "Your vote is in" : `You're entered in ${c.title}`}. {c.sponsor_name && `Presented by ${c.sponsor_name}.`} Reply STOP to opt out.</p>
        </Card>
      )}
    </div>
  );
}
